import { Component, inject, input, signal } from '@angular/core';
import { ServicePortalApi } from '../../../api/api';
import { TestUser } from '../../../api/types';
import { Router, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { ColumnDescription, TableComponent } from '../../components/table';
import {
  SynAlertComponent,
  SynButtonComponent,
  SynIconComponent,
} from '@synergy-design-system/angular';
import { getUserData, hasTestUserManagerRole } from '../../../api/auth';

@Component({
  selector: 'app-client-test-users',
  template: `
    <div class="flex-row gap-1">
      <h1 i18n="@@test-users">Test Users</h1>
      @if (canCreateTestUsers()) {
        <syn-button class="margin-vert-center" size="small"
                    routerLink="/test-users/new"
                    [queryParams]="{ application: clientId() }">
          <syn-icon name="add_fill" slot="prefix" />
          <span i18n="@@create-new-test-user">Create New Test User</span>
        </syn-button>
      }
    </div>

    <syn-alert [open]="true" variant="primary">
      <syn-icon name="info_fill" slot="icon" />
      <span i18n="@@test-user-access-help">
        You can see all test users that are associated with the client.
        If you cannot edit the test user, you can request access from the owners.
      </span>
    </syn-alert>

    <ng-template #rowTemplate let-value>
      <!-- Enable type checking in the template -->
      @let entry = identity(value);
      <tr>
        <td>{{ entry.username }}</td>
        <td>{{ entry.email }}</td>
        <td>{{ entry.lastLogin }}</td>
        <td>{{ ownerList(entry) }}</td>
        <td>
          @if (isOwnerOf(entry)) {
            <syn-button
              (click)="openUser(entry)"
            >
              <span i18n="@@edit-test-user">Edit Test User</span>
            </syn-button>
          } @else {
            <!-- The current user is not an owner, so we just show a link to ask the owners -->
            <a class="syn-link" [href]="requestAccessLink(entry)">
              <span i18n="@@request-access">Request Access</span>
            </a>
          }
        </td>
      </tr>
    </ng-template>
    <app-table
      [columns]="columns"
      [values]="users()"
      [pageSize]="20"
      [clickable]="false"
      [rowTemplate]="rowTemplate"
    />
  `,
  styles: [
    `
    `,
  ],
  imports: [
    RouterLink,
    SynButtonComponent,
    SynIconComponent,
    TableComponent,
    SynAlertComponent,
  ],
})
export class ClientTestUsersComponent {
  api = inject(ServicePortalApi);
  router = inject(Router);
  location = inject(Location);

  clientId = input<string>('');
  users = input<TestUser[]>([]);

  currentUser = signal(getUserData());
  canCreateTestUsers = signal(hasTestUserManagerRole());

  columns: ColumnDescription<TestUser>[] = [
    {
      label: $localize`:@@username:Username`,
      getField: (user) => user.username,
      style: 'width: 20%',
    },
    {
      label: $localize`:@@email:Email`,
      getField: (user) => user.email ?? '',
      style: 'width: 10%',
    },
    {
      label: $localize`:@@last-login:Last Login`,
      getField: (user) => user.lastLogin ?? '',
      style: 'width: 15%',
    },
    {
      label: $localize`:@@owners:Owners`,
      getField: this.ownerList,
      style: 'width: 40%',
    },
    {
      label: $localize`:@@action:Action`,
      getField: () => '',
      style: 'width: 15%',
    },
  ];

  openUser(user: TestUser) {
    void this.router.navigate(['test-users', user.id]).then(() => {
    });
  }

  ownerList(user: TestUser) {
    return user.owners.map((o) => o.username).join(', ');
  }

  isOwnerOf(user: TestUser) {
    return user.owners.some((owner) => owner.username === this.currentUser().username);
  }

  identity(user: TestUser): TestUser {
    return user;
  }

  requestAccessLink(user: TestUser): string {
    // NOTE: Currently, we just use the usernames as emails. This might not be always correct.
    //       But should work for 99% of all users that use the service portal.
    const recipients = user.owners.map((o) => o.username).join('; ');
    const subject = 'Request Access for Test User';
    const testUserLink = window.location.origin + '/test-users/' + user.id + '/permissions'
      + '?addOwner=' + this.currentUser().username;
    const body = `Dear owners of the test user ${user.username},

Can you grant me permissions to access the test user ${user.username}?

You can use the following link to navigate to the test user and add me (${this.currentUser().username}) to the owner list:
${testUserLink}

Best regards,
${this.currentUser().givenName + ' ' + this.currentUser().familyName}
`;
    return `mailto:${recipients}?subject=${subject}&body=${encodeURIComponent(body)}`;
  }
}
